import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  CustomCakeData,
  getSelectedCakeLineTotal,
  getSelectedCakeUnitPrice,
  getSelectedCakesTotal,
} from './CustomCakeBuilder';
import CakeDetailsDialogWrapper from './CakeDetailsDialogWrapper';
import { useAppSelector, useAppDispatch } from '../../../../store';
import { findToppingsCategoryItem } from '../../../../store/toppingsCategoryItem/api';

interface SelectedCakesListProps {
  cakes: CustomCakeData[];
  onRemove: (index: number) => void;
  onEdit: (index: number, cake: CustomCakeData) => void;
  onQuantityChange: (index: number, newQty: number) => void;
}

const SelectedCakesList: React.FC<SelectedCakesListProps> = ({
  cakes,
  onRemove,
  onEdit,
  onQuantityChange,
}) => {
  const dispatch = useAppDispatch();
  const [selectedCakeForDetails, setSelectedCakeForDetails] = useState<{ cake: CustomCakeData; index: number } | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  
  const { data: shapes } = useAppSelector((state) => state.shape);
  const { data: flavors } = useAppSelector((state) => state.flavor);
  const { data: toppingItems, status: toppingItemsStatus } = useAppSelector((state) => state.toppingsCategoryItem);

  // Ensure topping items are loaded when component mounts or when cakes have toppings
  useEffect(() => {
    const hasToppings = cakes.some((cake) => cake.toppingId);
    if (hasToppings && toppingItemsStatus === 'idle' && (!toppingItems || toppingItems.length === 0)) {
      // Load all topping items if any cake has a topping and they're not loaded
      dispatch(
        findToppingsCategoryItem({
          limit: 100,
        })
      );
    }
  }, [dispatch, cakes, toppingItems, toppingItemsStatus]);

  // Also load topping items when details dialog opens (in case they weren't loaded yet)
  useEffect(() => {
    if (detailsDialogOpen && selectedCakeForDetails?.cake.toppingId) {
      if (toppingItemsStatus === 'idle' && (!toppingItems || toppingItems.length === 0)) {
        dispatch(
          findToppingsCategoryItem({
            limit: 100,
          })
        );
      }
    }
  }, [detailsDialogOpen, selectedCakeForDetails, dispatch, toppingItems, toppingItemsStatus]);

  const totalAmount = getSelectedCakesTotal(cakes);

  const handleViewDetails = (cake: CustomCakeData, index: number) => {
    setSelectedCakeForDetails({ cake, index });
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedCakeForDetails(null);
  };

  if (cakes.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant='h6' gutterBottom>
          Selected Cakes
        </Typography>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant='body2' color='text.secondary'>
            No cakes added yet. Add cakes using the builder above.
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>
          Selected Cakes ({cakes.length})
        </Typography>
        <Typography variant='h6' color='primary'>
          Total: {totalAmount.toFixed(2)} QAR
        </Typography>
      </Box>

      <TableContainer
        sx={{
          maxHeight: 500,
          overflowX: 'auto',
          overflowY: 'auto',
        }}
      >
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Shape</TableCell>
              <TableCell>Flavor</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Topping</TableCell>
              <TableCell>Write on Cake</TableCell>
              <TableCell>Instructions</TableCell>
              <TableCell>Packaging</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell align='center'>Qty</TableCell>
              <TableCell align='right'>Unit Price</TableCell>
              <TableCell align='right'>Total</TableCell>
              <TableCell align='center'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cakes.map((cake, index) => (
              <TableRow key={index} hover>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant='body2'>{cake.shapeLabelEn}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {cake.shapeLabelAr}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{cake.flavorLabelEn}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {cake.flavorLabelAr}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: cake.colorCode,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant='body2'>{cake.colorCode}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {cake.toppingId ? (
                    <>
                      <Typography variant='body2'>{cake.toppingLabelEn || 'Topping'}</Typography>
                      {cake.toppingLabelAr && (
                        <Typography variant='caption' color='text.secondary'>
                          {cake.toppingLabelAr}
                        </Typography>
                      )}
                    </>
                  ) : cake.printImageUrl ? (
                    <Chip label='Custom Image' size='small' color='secondary' />
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Without (بدون)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={cake.writeOnCake || '-'} arrow>
                    <Typography 
                      variant='body2' 
                      sx={{ 
                        maxWidth: 120,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cake.writeOnCake || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={cake.instruction || '-'} arrow>
                    <Typography 
                      variant='body2' 
                      sx={{ 
                        maxWidth: 120,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cake.instruction || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {cake.cakeBoxLabelEn || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={cake.displayMode.toUpperCase()}
                    size='small'
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell align='center'>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <IconButton
                      size='small'
                      onClick={() => onQuantityChange(index, Math.max(1, cake.qty - 1))}
                      disabled={cake.qty <= 1}
                    >
                      <RemoveIcon fontSize='small' />
                    </IconButton>
                    <Typography variant='body2' sx={{ minWidth: 30, textAlign: 'center' }}>
                      {cake.qty}
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={() => onQuantityChange(index, cake.qty + 1)}
                    >
                      <AddIcon fontSize='small' />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align='right'>
                  {getSelectedCakeUnitPrice(cake).toFixed(2)} QAR
                </TableCell>
                <TableCell align='right'>
                  <Typography variant='body2' fontWeight='bold'>
                    {getSelectedCakeLineTotal(cake).toFixed(2)} QAR
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title='View Details'>
                      <IconButton
                        size='small'
                        color='info'
                        onClick={() => handleViewDetails(cake, index)}
                      >
                        <VisibilityIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Edit'>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={() => onEdit(index, cake)}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Remove'>
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() => onRemove(index)}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cake Details Dialog */}
      {selectedCakeForDetails && (
        <CakeDetailsDialogWrapper
          cake={selectedCakeForDetails.cake}
          cakeIndex={selectedCakeForDetails.index}
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          onEdit={onEdit}
          onRemove={onRemove}
          onQuantityChange={onQuantityChange}
          shapes={shapes}
          flavors={flavors}
          toppingItems={toppingItems}
        />
      )}

      {cakes.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Total Cakes: {cakes.reduce((sum, cake) => sum + cake.qty, 0)}
            </Typography>
            <Typography variant='h6' color='primary'>
              Grand Total: {totalAmount.toFixed(2)} QAR
            </Typography>
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default SelectedCakesList;

